// File: src/pages/CreateSalesOrderInvoice.jsx
import React, { useEffect, useState, useContext, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Card,
  CardBody,
  Col,
  Form,
  Row,
  Input,
  Label,
  Button,
  Table,
  Spinner,
} from "reactstrap";
import Multiselect from "multiselect-react-dropdown";
import {
  _Get,
  _PostSave,
  _PutUpdate,
  CreateAccountList, // Delivery Boy list
} from "../import/ApiEndPoint/ApiCalling";
import {
  Create_Existing_Invoice,
  EmergencyInvoice,
  viewEmergencyInvoice,
  updateEmergencyInvoice,
  Sales_Edit_Order2, // update order status/delivery boy
} from "../import/ApiEndPoint/Api";
import UserContext from "../import/context/Context";
import { GstCalculation } from "../import/GstCalculation";
import swal from "sweetalert";
import "./InvoiceUI.css";

/* ----------------------- Helpers: Rounding & GST ------------------- */
const round2 = (n) => Number((Number(n) || 0).toFixed(2));
const floor2 = (n) => Math.floor((Number(n) || 0) * 100) / 100;
const ceil2 = (n) => Math.ceil((Number(n) || 0) * 100) / 100;

const parseGstPct = (g) => {
  if (g === null || g === undefined) return 0;
  if (typeof g === "number") return g || 0;
  const s = String(g).trim();
  if (!s) return 0;
  return s
    .split("+")
    .map((x) => Number(x) || 0)
    .reduce((a, b) => a + b, 0);
};

const saleFromMRPByGrade = (mrp, gradePct) =>
  floor2(Number(mrp || 0) / (1 + Number(gradePct || 0) / 100));
const basicFromSaleGST = (sale, gstPct) =>
  ceil2(Number(sale || 0) / (1 + Number(gstPct || 0) / 100));
const saleFromBasic = (basic, gstPct) =>
  round2(Number(basic || 0) * (1 + Number(gstPct || 0) / 100));
const basicFromSaleEditable = (sale, gstPct) => basicFromSaleGST(sale, gstPct);

/* ---------------------- Adjustments Calculator --------------------- */
const computeAdjustments = (base = 0, discountsRows = [], chargesRows = []) => {
  let running = Number(base) || 0;
  const discApplied = [];
  const chgApplied = [];

  // DISCOUNTS (sequential)
  (discountsRows || []).forEach((r) => {
    const row = { ...r };
    const typ = (row?.discounttype || "").toLowerCase();
    if (typ === "percentage") {
      const val = (running * (Number(row?.percentage) || 0)) / 100;
      row.__applied = ceil2(val);
    } else {
      row.__applied = round2(Number(row?.amount) || 0);
    }
    running -= row.__applied;
    if (running < 0) running = 0;
    discApplied.push(row);
  });

  // CHARGES (sequential)
  (chargesRows || []).forEach((r) => {
    const row = { ...r };
    const typ = (row?.discounttype || "").toLowerCase();
    if (typ === "percentage") {
      const val = (running * (Number(row?.percentage) || 0)) / 100;
      row.__applied = round2(val);
    } else {
      row.__applied = round2(Number(row?.amount) || 0);
    }
    running += row.__applied;
    chgApplied.push(row);
  });

  const discountTotal = round2(
    discApplied.reduce((a, x) => a + (x.__applied || 0), 0)
  );
  const chargesTotal = round2(
    chgApplied.reduce((a, x) => a + (x.__applied || 0), 0)
  );

  return {
    discountTotal,
    chargesTotal,
    discountRowsApplied: discApplied,
    chargeRowsApplied: chgApplied,
    netAfterAdj: round2(running), // Basic - Discount + Charges
  };
};

/* --------------------------- Component ----------------------------- */

let geotagging = "";

/* ======= small localStorage cache (speeds up TTI) ======= */
const getCache = (key, maxAgeMs = 5 * 60 * 1000) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { ts, data } = JSON.parse(raw);
    if (!ts || Date.now() - ts > maxAgeMs) return null;
    return data;
  } catch {
    return null;
  }
};
const setCache = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
  } catch {}
};

const SALES_PAGE_SUMMARY_URL = "/order/sales-page-summary/";
const salesPageSummaryCache =
  window.__dmsSalesPageSummaryCache ||
  (window.__dmsSalesPageSummaryCache = new Map());
const salesPageBootstrapCache =
  window.__dmsSalesPageBootstrapCache ||
  (window.__dmsSalesPageBootstrapCache = new Map());
const SALES_CUSTOMER_OPTIONS_URL = "/order/sales-customer-options/";
const SALES_CUSTOMER_BALANCE_URL = "/order/sales-customer-balance/";
const salesCustomerOptionsCache =
  window.__dmsSalesCustomerOptionsCache ||
  (window.__dmsSalesCustomerOptionsCache = new Map());
const salesCustomerBalanceCache =
  window.__dmsSalesCustomerBalanceCache ||
  (window.__dmsSalesCustomerBalanceCache = new Map());

const clearSalesFastCaches = () => {
  salesPageSummaryCache.clear();
  salesPageBootstrapCache.clear();
  salesCustomerOptionsCache.clear();
  salesCustomerBalanceCache.clear();
};

const getSalesPageSummary = async (database) => {
  if (!database) return {};
  const now = Date.now();
  const cached = salesPageSummaryCache.get(database);
  if (cached && now - cached.time < 120000) return cached.promise;
  const promise = _Get(SALES_PAGE_SUMMARY_URL, encodeURIComponent(database)).catch((error) => {
    salesPageSummaryCache.delete(database);
    throw error;
  });
  salesPageSummaryCache.set(database, { time: now, promise });
  return promise;
};


const selectedSalesDatabase = (baseDatabase) => {
  const fy = localStorage.getItem("financialYearSelected");
  return fy ? `${baseDatabase}-${fy}` : baseDatabase;
};

const getSalesPageBootstrapFast = async (baseDatabase, rootUserId) => {
  const financialYear = localStorage.getItem("financialYearSelected");
  if (!baseDatabase || !rootUserId || !financialYear) return {};
  const key = `${baseDatabase}:${financialYear}:${rootUserId}`;
  const now = Date.now();
  const cached = salesPageBootstrapCache.get(key);
  if (cached && now - cached.time < 120000) return cached.promise;

  const url = `/order/sales-page-bootstrap/${encodeURIComponent(
    rootUserId,
  )}/${encodeURIComponent(baseDatabase)}?financialYear=${encodeURIComponent(
    financialYear,
  )}&includeCustomers=0`;
  const selectedDatabase = `${baseDatabase}-${financialYear}`;
  const promise = _Get(url, "")
    .then((response) => response || {})
    .catch((error) => {
      salesPageBootstrapCache.delete(key);
      salesPageSummaryCache.delete(selectedDatabase);
      throw error;
    });

  salesPageSummaryCache.set(selectedDatabase, {
    time: now,
    promise: promise.then((response) => response?.summary || {}),
  });
  salesPageBootstrapCache.set(key, { time: now, promise });
  return promise;
};


const formatSalesCustomerOption = (item = {}) => ({
  ...item,
  displayLabel: `${item?.CompanyName || item?.fullName || "Customer"} (Grade: ${
    item?.category?.grade ? String(item.category.grade).toUpperCase() : "NA"
  })`,
});

const getSalesCustomerOptions = async (
  database,
  rootUserId,
  searchText = "",
) => {
  if (!database || !rootUserId) return [];
  const q = String(searchText || "").trim();
  const key = `${database}:${rootUserId}:${q.toLowerCase()}`;
  const now = Date.now();
  const cached = salesCustomerOptionsCache.get(key);
  if (cached && now - cached.time < (q ? 15000 : 60000)) {
    return cached.promise;
  }

  const url = `${SALES_CUSTOMER_OPTIONS_URL}${encodeURIComponent(
    rootUserId,
  )}/${encodeURIComponent(database)}?limit=${q ? 100 : 120}&q=${encodeURIComponent(q)}`;

  const promise = _Get(url, "")
    .then((response) =>
      (Array.isArray(response?.customers) ? response.customers : []).map(
        formatSalesCustomerOption,
      ),
    )
    .catch((error) => {
      salesCustomerOptionsCache.delete(key);
      throw error;
    });

  salesCustomerOptionsCache.set(key, { time: now, promise });
  return promise;
};

const getSalesCustomerBalance = async (partyId, selectedDatabase) => {
  if (!partyId || !selectedDatabase) return {};
  const key = `${selectedDatabase}:${partyId}`;
  const now = Date.now();
  const cached = salesCustomerBalanceCache.get(key);
  if (cached && now - cached.time < 15000) return cached.promise;

  const url = `${SALES_CUSTOMER_BALANCE_URL}${encodeURIComponent(
    partyId,
  )}/${encodeURIComponent(selectedDatabase)}`;
  const promise = _Get(url, "").catch((error) => {
    salesCustomerBalanceCache.delete(key);
    throw error;
  });
  salesCustomerBalanceCache.set(key, { time: now, promise });
  return promise;
};


const CreateSalesOrderInvoice = () => {
  const { id: orderId } = useParams();
  const navigate = useNavigate();
  const Context = useContext(UserContext);

  /* ===== rounding ===== */
  const roundHalfUp = (num) => {
    const frac = num - Math.floor(num);
    return frac > 0.49 ? Math.ceil(num) : Math.floor(num);
  };

  /* ===== page loader ===== */
  const [pageLoading, setPageLoading] = useState(true);

  /* ===== invoice states ===== */
  const [localRoundOff, setLocalRoundOff] = useState(0);
  const [InvoiceNumber, setInvoiceNumber] = useState("");
  const [OrderNumber, setOrderNumber] = useState(""); // readonly display
  const [ARN, setARN] = useState("");
  const [CustomerLimit, setCustomerLimit] = useState(0);
  const [openingLimit, setOpeningLimit] = useState(0);
  const [CustomerTerm, setCustomerTerm] = useState("");
  const [PartyLogin, setPartyLogin] = useState(false);
  const [Loading, setLoading] = useState(false);
  const [borderColor, setBorderColor] = useState("black");
  const [remarkMessage, setRemarkMessage] = useState("");
  const [remarkModel, setRemarkModel] = useState(false);

  const [ProductList, setProductList] = useState([]);
  const [GSTData, setGSTData] = useState({});
  const [PartyList, setPartyList] = useState([]);
  const [PartyId, setPartyId] = useState("");
  const [Party, setParty] = useState({});
  const [UnitList, setUnitList] = useState([]);
  const [chargesMaster, setChargesMaster] = useState([]);

  const [emergencyInvoice, setEmergencyInvoice] = useState(false);
  const [emergencyStatus, setEmergencyStatus] = useState("");
  const [lastEntryId, setLastEntryId] = useState("");
  const [UserInfo, setUserInfo] = useState({});

  // Additional fields
  const [NoOfPackage, setNoOfPackage] = useState(0);

  // DELIVERY BOY LIST + selection
  const [deliveryBoyOptions, setDeliveryBoyOptions] = useState([]);
  const [deliveryBoy, setDeliveryBoy] = useState("");

  // Bill-level rows
  const [selectedChargesRows, setSelectedChargesRows] = useState([]);
  const [selectedDiscountRows, setSelectedDiscountRows] = useState([]);

  // Totals (Taxable/Grand)
  const [finalPrice, setFinalPrice] = useState(0); // Taxable Value
  const [taxRate, setTaxRate] = useState(0); // GST on charges
  const [finalPriceAfterTax, setFinalPriceAfterTax] = useState(0); // Grand

  const [ledgerBalance, setLedgerBalance] = useState("");
  const [pendingData, setPendingData] = useState(0);
  const [orderDate, setOrderDate] = useState("");

  const [product, setProduct] = useState([
    {
      productId: "",
      productData: "",
      disCountPercentage: 0,
      availableQty: 0,
      wholeQty: 0,
      qty: 1,
      mrp: 0,
      gradeDiscountPct: 0,
      price: 0,
      basicPrice: 0,
      salePrice: 0,
      Size: 0,
      unitType: "",
      unitPriceAfterDiscount: "",
      totalprice: "",
      taxableAmount: 0,
      gstPercentage: 0,
      sgstRate: 0,
      cgstRate: 0,
      igstRate: 0,
      grandTotal: 0,
      HSN_Code: "",
      secondaryUnit: "",
      GrossQty: 0,
      primaryUnit: "",
    },
  ]);

  const multiselectRefs = useRef(product.map(() => React.createRef()));

  /* ---------------- compact CSS injector ---------------- */
  useEffect(() => {
    const id = "invoice-compact-css";
    if (document.getElementById(id)) return;
    const s = document.createElement("style");
    s.id = id;
    s.textContent = `
      .compact-ui{
        --box-w: clamp(68px, 6.8vw, 110px);
        --gap: 6px; --fs: 13px; --pad: 6px; --label-fs: 12px;
      }
      .compact-ui label{ font-size: var(--label-fs); margin-bottom: 4px; font-weight: 600; line-height: 1.1; }
      .compact-ui .card-body{ padding: 12px; }
      .compact-ui .form-control,.compact-ui .form-select{ height: calc(28px + var(--pad)); min-height: calc(28px + var(--pad)); padding: var(--pad) 10px; font-size: var(--fs); border-radius: 10px; line-height: 1.15; }
      .compact-ui .btn{ --bs-btn-padding-y: 4px; --bs-btn-padding-x: 10px; font-size: 12px; line-height: 1.1; border-radius: 10px; }
      .compact-ui .btn-sm{ padding: 3px 8px; font-size: 11.5px; border-radius: 9px; }
      .compact-ui .table, .compact-ui table{ font-size: 12px; }
      .compact-ui .table > :not(caption) > * > *{ padding: 4px 6px; }
      .compact-ui .inline-row{ display: grid; grid-auto-flow: column; grid-auto-columns: var(--box-w); gap: var(--gap); align-items: end; overflow-x: hidden; }
      .compact-ui .auto-cell{ width: var(--box-w); min-width: var(--box-w); }
      .compact-ui .chip{ display: inline-flex; align-items: center; gap: 2px; padding: 2px 4px; border-radius: 9999px; font-size: 12px; background: #f3f4f6; border: 1px solid #e5e7eb; }
      .invoice-loader{ position: fixed; inset: 0; background: rgba(255,255,255,0.75); z-index: 9999; display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 10px;}
    `;
    document.head.appendChild(s);
  }, []);
  useEffect(() => {
    const id = "multiselect-chip-fix";
    if (document.getElementById(id)) return;
    const s = document.createElement("style");
    s.id = id;
    s.textContent = `
      .multiselect-container .searchWrapper{ overflow: visible !important; }
      .multiselect-container .chip{ display: inline-flex !important; align-items: center; gap: 6px; }
      .multiselect-container .chip > span{ white-space: nowrap !important; }
      /* Make customer/product names blue */
      .multiselect-container .chip, .multiselect-container .chip > span { color: #0d6efd !important; }
      .multiselect-container .optionListContainer li { color: #0d6efd !important; }
    `;
    document.head.appendChild(s);
  }, []);

  /* Charges now come from the Sales bootstrap response. */


  /* ---------------- helpers ---------------- */
  const sumLineBasics = (rows) =>
    (rows || []).reduce(
      (s, r) =>
        s + Number(r?.qty || 0) * Number(r?.basicPrice || r?.price || 0),
      0
    );

  /* ============================ TOTALS ============================ */
  const recalcTotals = (
    _chargesRows = selectedChargesRows,
    _discountRows = selectedDiscountRows
  ) => {
    const basicTotal = sumLineBasics(product);

    const {
      chargesTotal,
      discountTotal,
      discountRowsApplied,
      chargeRowsApplied,
      netAfterAdj,
    } = computeAdjustments(basicTotal, _discountRows, _chargesRows);

    setSelectedDiscountRows(discountRowsApplied);
    setSelectedChargesRows(chargeRowsApplied);

    // GST on charges at highest GST% among items
    const maxItemGST =
      product.reduce(
        (m, p) => Math.max(m, Number(parseGstPct(p?.gstPercentage || 0))),
        0
      ) || 0;

    const isIGST = !!GSTData?.Tax?.IgstTaxType;
    const chargesGST = (chargesTotal * maxItemGST) / 100;
    const half = chargesGST / 2;

    let addIGST = 0,
      addCGST = 0,
      addSGST = 0;
    if (isIGST) addIGST = chargesGST;
    else {
      addCGST = half;
      addSGST = half;
    }

    // Taxable Value
    setFinalPrice(Number(netAfterAdj).toFixed(2));
    // GST on charges (stored as single number; split for SGST/CGST when needed)
    setTaxRate(Number(addIGST + addCGST + addSGST));

    // Items GST from GstCalculation
    const itemsGST = isIGST
      ? Number(GSTData?.Tax?.IgstTotal || 0)
      : Number(GSTData?.Tax?.CgstTotal || 0) +
        Number(GSTData?.Tax?.SgstTotal || 0);

    const gross =
      Number(netAfterAdj || 0) +
      Number(itemsGST || 0) +
      Number(addIGST + addCGST + addSGST);

    const rounded = roundHalfUp(gross);
    const roundOff = Number(Math.abs(rounded - gross).toFixed(2));

    setLocalRoundOff(roundOff);
    setFinalPriceAfterTax(rounded);
  };

  /* ---------------- HSN Summary ---------------- */
  const hsnSummary = useMemo(() => {
    const details = Array.isArray(GSTData?.gstDetails)
      ? GSTData.gstDetails
      : [];
    const isIGST = !!GSTData?.Tax?.IgstTaxType;

    // Group ITEMS by HSN
    const byHSN = new Map();
    details.forEach((r) => {
      const hsn = String(r?.hsn || "NA");
      const ratePct = parseGstPct(r?.gstPercentage || 0);
      const g = byHSN.get(hsn) || {
        hsn,
        ratePct,
        taxable: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
      };
      g.ratePct = ratePct;
      g.taxable += Number(r?.taxable || 0);
      g.cgst += Number(r?.cgstRate || 0);
      g.sgst += Number(r?.sgstRate || 0);
      g.igst += Number(r?.igstRate || 0);
      byHSN.set(hsn, g);
    });

    let maxRate = 0;
    for (const v of byHSN.values()) maxRate = Math.max(maxRate, v.ratePct);

    const chargesAmt = (selectedChargesRows || []).reduce(
      (s, x) => s + (Number(x?.__applied) || 0),
      0
    );
    const chargesTax = (chargesAmt * maxRate) / 100;

    const rows = Array.from(byHSN.values())
      .map((r) => ({
        kind: "ITEM",
        hsn: r.hsn,
        ratePct: r.ratePct,
        taxable: round2(r.taxable),
        cgst: round2(r.cgst),
        sgst: round2(r.sgst),
        igst: round2(r.igst),
        total: round2(r.taxable + r.cgst + r.sgst + r.igst),
      }))
      .sort((a, b) => b.ratePct - a.ratePct);

    if (chargesAmt > 0) {
      rows.push({
        kind: "CHARGE",
        hsn: `CHARGES @ ${maxRate}%`,
        ratePct: maxRate,
        taxable: round2(chargesAmt),
        cgst: isIGST ? 0 : round2(chargesTax / 2),
        sgst: isIGST ? 0 : round2(chargesTax / 2),
        igst: isIGST ? round2(chargesTax) : 0,
        total: round2(chargesAmt + chargesTax),
      });
    }

    const totals = rows.reduce(
      (acc, r) => ({
        taxable: acc.taxable + r.taxable,
        cgst: acc.cgst + (r.cgst || 0),
        sgst: acc.sgst + (r.sgst || 0),
        igst: acc.igst + (r.igst || 0),
        total: acc.total + r.total,
      }),
      { taxable: 0, cgst: 0, sgst: 0, igst: 0, total: 0 }
    );

    return { rows, totals, isIGST };
  }, [GSTData, selectedChargesRows]);

  /* ---------------- Party selection ---------------- */
  const generateNextInvoiceId = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem("userData"));
      const summary = await getSalesPageSummary(selectedSalesDatabase(userData?.database));
      const nums = Array.isArray(summary?.completedInvoiceNumbers)
        ? summary.completedInvoiceNumbers.map(Number).filter(Number.isFinite)
        : [];
      const setNums = new Set(nums);
      let next = nums.length ? Math.max(...nums) + 1 : 1;
      while (setNums.has(next)) next += 1;
      return next.toString().padStart(3, "0");
    } catch {
      return "001";
    }
  };

  const unique3Digit = generateNextInvoiceId;

  const partySearchTimerRef = useRef(null);
  const partySearchRequestRef = useRef(0);

  const handlePartySearch = (searchText) => {
    if (partySearchTimerRef.current) {
      window.clearTimeout(partySearchTimerRef.current);
    }
    const requestNo = ++partySearchRequestRef.current;
    partySearchTimerRef.current = window.setTimeout(async () => {
      try {
        const userData = JSON.parse(localStorage.getItem("userData") || "{}");
        const hierarchyRoot =
          userData?.rolename?.roleName === "SuperAdmin"
            ? userData?._id
            : userData?.created_by || userData?._id;
        const customers = await getSalesCustomerOptions(
          userData?.database,
          hierarchyRoot,
          searchText,
        );
        if (requestNo === partySearchRequestRef.current) {
          setPartyList(customers);
        }
      } catch {}
    }, 180);
  };

  const handleSelectionParty = async (_selectedList, selectedItem) => {
    const label =
      selectedItem?.displayLabel ||
      `${selectedItem?.CompanyName || selectedItem?.fullName || "Party"}${
        selectedItem?.category?.grade
          ? ` (Grade: ${String(selectedItem?.category?.grade).toUpperCase()})`
          : ""
      }`;
    setOpeningLimit(selectedItem?.limit);
    setParty({ ...selectedItem, displayLabel: label });

    // Fast path: do not rebuild the full live ledger here.
    setLedgerBalance("");
    setPendingData(0);

    const uniqueNumber = await unique3Digit();
    setInvoiceNumber(uniqueNumber);
    setPartyId(selectedItem._id);

    const partyGrade = Number(selectedItem?.category?.discount || 0);
    setProduct((prev) =>
      prev.map((row) => {
        if (!row?.productId || !row?.productData) return row;
        const mrp = Number(row?.productData?.Product_MRP || 0);
        const gstPct = Number(row?.productData?.GSTRate || 0);
        const sale = saleFromMRPByGrade(mrp, partyGrade);
        const basic = basicFromSaleGST(sale, gstPct);
        return {
          ...row,
          mrp,
          gradeDiscountPct: partyGrade,
          gstPercentage: gstPct,
          salePrice: sale,
          basicPrice: basic,
          price: basic,
          taxableAmount: round2((row?.qty || 0) * (basic || 0)),
        };
      })
    );

    const gstdetails = GstCalculation(
      selectedItem,
      product,
      Context,
      selectedDiscountRows
    );
    setGSTData(gstdetails);

    const isCash = (selectedItem?.paymentTerm || "")
      .toLowerCase()
      .includes("cash");

    if (isCash) {
      setCustomerLimit(0);
      setCustomerTerm("Cash");
      setBorderColor("green");
    } else {
      try {
        const userData = JSON.parse(localStorage.getItem("userData") || "{}");
        const selectedDatabase = selectedSalesDatabase(userData?.database);
        const balance = await getSalesCustomerBalance(
          selectedItem?._id,
          selectedDatabase,
        );

        const adjustedPending = Number(balance?.adjustedPending || 0);
        const finalLimit = Number(
          balance?.availableLimit ?? selectedItem?.limit ?? 0,
        );

        setLedgerBalance(
          balance?.ledgerBalance ||
            `${Math.abs(Number(balance?.netBalance || 0))} (${
              balance?.balanceType || "Debit"
            })`,
        );
        setPendingData(adjustedPending);
        setCustomerLimit(finalLimit);

        const gtot = Number(gstdetails?.Tax?.GrandTotal || 0);
        setBorderColor(gtot < finalLimit ? "green" : "red");
        setCustomerTerm("");
      } catch {
        setCustomerLimit(selectedItem?.limit || 0);
        setBorderColor("green");
      }
    }

    const newDetails = GstCalculation(
      selectedItem,
      product,
      Context,
      selectedDiscountRows
    );
    setGSTData(newDetails);
    recalcTotals(selectedChargesRows, selectedDiscountRows);
  };

  const onRemoveParty = () => {
    setPartyId("");
    setParty({});
    setGSTData({});
    setCustomerLimit(0);
    setBorderColor("black");
    multiselectRefs.current.forEach((ref) =>
      ref?.current?.resetSelectedValues?.()
    );
    setProduct([
      {
        productId: "",
        productData: "",
        disCountPercentage: 0,
        availableQty: 0,
        wholeQty: 0,
        qty: 1,
        mrp: 0,
        gradeDiscountPct: 0,
        price: 0,
        basicPrice: 0,
        salePrice: 0,
        Size: 0,
        unitType: "",
        unitPriceAfterDiscount: "",
        totalprice: "",
        taxableAmount: 0,
        gstPercentage: 0,
        sgstRate: 0,
        cgstRate: 0,
        igstRate: 0,
        grandTotal: 0,
        HSN_Code: "",
        secondaryUnit: "",
        GrossQty: 0,
        primaryUnit: "",
      },
    ]);
    setSelectedChargesRows([]);
    setSelectedDiscountRows([]);
    setFinalPrice(0);
    setTaxRate(0);
    setFinalPriceAfterTax(0);
  };

  /* ---------------- Product selection ---------------- */
  const handleSelection = async (_selectedList, selectedItem, index) => {
    if (!(selectedItem?.qty > 0)) {
      swal("Error", "Stock Not Available", "error");
      return;
    }

    const partyGrade = Number(Party?.category?.discount || 0);
    const mrp = Number(selectedItem?.Product_MRP || 0);
    const gstPct = Number(selectedItem?.GSTRate || 0);

    const sale = saleFromMRPByGrade(mrp, partyGrade);
    const basic = basicFromSaleGST(sale, gstPct);

    setProduct((prev) => {
      const updated = [...prev];
      const row = { ...(updated[index] || {}) };

      row.productId = selectedItem?._id;
      row.productData = selectedItem;
      row.mrp = mrp;
      row.gradeDiscountPct = partyGrade;
      row.gstPercentage = gstPct;
      row.salePrice = sale;
      row.basicPrice = basic;
      row.price = basic;

      row.HSN_Code = selectedItem?.HSN_Code;
      row.primaryUnit = selectedItem?.primaryUnit;
      row.secondaryUnit = selectedItem?.secondaryUnit;
      row.secondarySize = selectedItem?.secondarySize;
      row.GrossQty = Number(
        (1 / (selectedItem?.secondarySize || 1)).toFixed(3)
      );

      row.disCountPercentage = partyGrade;
      row.availableQty = selectedItem?.qty;
      row.wholeQty = selectedItem?.qty;

      // BASIC TOTAL = basic * qty
      row.taxableAmount = round2((row.qty || 0) * (row.basicPrice || 0));

      updated[index] = row;

      const gstdetails = GstCalculation(
        Party,
        updated,
        Context,
        selectedDiscountRows
      );
      setGSTData(gstdetails);

      const det = gstdetails?.gstDetails?.[index] || {};
      row.sgstRate = det?.sgstRate || 0;
      row.cgstRate = det?.cgstRate || 0;
      row.igstRate = det?.igstTax?.[0]?.amount || 0;
      row.grandTotal = det?.grandTotal || 0;

      updated[index] = row;
      setProduct(updated);
      recalcTotals();

      return updated;
    });
  };

  const onRemove1 = (_selectedList, _removedItem, index) => {
    const updated = [...product];
    updated[index] = {
      productId: "",
      productData: "",
      disCountPercentage: 0,
      availableQty: 0,
      wholeQty: 0,
      qty: 1,
      mrp: 0,
      gradeDiscountPct: 0,
      price: 0,
      basicPrice: 0,
      salePrice: 0,
      Size: 0,
      unitType: "",
      unitPriceAfterDiscount: "",
      totalprice: "",
      taxableAmount: 0,
      gstPercentage: 0,
      sgstRate: 0,
      cgstRate: 0,
      igstRate: 0,
      grandTotal: 0,
      HSN_Code: "",
      secondaryUnit: "",
      GrossQty: 0,
      primaryUnit: "",
    };
    setProduct(updated);

    const gstdetails = GstCalculation(
      Party,
      updated,
      Context,
      selectedDiscountRows
    );
    setGSTData(gstdetails);
    recalcTotals();
  };

  /* ---------------- Qty & price handlers ---------------- */
  const handleRequredQty = (e, index, availableQty, CustomerTermState) => {
    const list = [...product];
    const { name, value } = e.target;

    const asNum = value === "" ? "" : Number(value);
    if (value !== "" && isNaN(asNum)) return;

    const nextNetQty =
      name === "GrossQty"
        ? Number(((list[index]?.secondarySize || 1) * (asNum || 0)).toFixed(3))
        : asNum || 0;

    if (value !== "" && (nextNetQty < 0 || nextNetQty > (availableQty || 0))) {
      return;
    }

    if (name === "GrossQty") {
      list[index].GrossQty = value === "" ? 0 : asNum;
      list[index].qty = nextNetQty;
    } else {
      list[index][name] = value === "" ? 0 : asNum;
      list[index].GrossQty = Number(
        ((list[index].qty || 0) / (list[index]?.secondarySize || 1)).toFixed(3)
      );
    }

    const gstdetails = GstCalculation(
      Party,
      list,
      Context,
      selectedDiscountRows
    );
    setGSTData(gstdetails);

    const gstInfo = gstdetails?.gstDetails?.[index] || {};
    // BASIC TOTAL = basic * qty
    list[index].taxableAmount = round2(
      (list[index]?.qty || 0) *
        (list[index]?.basicPrice || list[index]?.price || 0)
    );
    list[index].sgstRate = gstInfo?.sgstRate || 0;
    list[index].cgstRate = gstInfo?.cgstRate || 0;
    list[index].igstRate = gstInfo?.igstTax?.[0]?.amount || 0;
    list[index].grandTotal = gstInfo?.grandTotal || 0;
    list[index].gstPercentage = gstInfo?.gstPercentage || 0;
    list[index].disCountPercentage = gstInfo?.discountPercentage || 0;

    setProduct(list);

    if (CustomerTermState !== "Cash") {
      if ((gstdetails?.Tax?.GrandTotal || 0) < (CustomerLimit || 0)) {
        setBorderColor("green");
      } else {
        setBorderColor(emergencyStatus === "Active" ? "green" : "red");
      }
    }

    recalcTotals();
  };

  const handleChangePrice = (e, index) => {
    const { name, value } = e.target;
    const v = value === "" ? "" : Number(value);
    if (value !== "" && (isNaN(v) || v <= 0)) return;

    const list = [...product];
    if (!list[index]) return;

    const row = { ...list[index] };
    const gstPct = parseGstPct(row?.gstPercentage);

    if (name === "basicPrice") {
      row.basicPrice = v === "" ? 0 : v;
      row.price = v === "" ? 0 : v;
      row.salePrice = saleFromBasic(v || 0, gstPct);
    } else if (name === "salePrice") {
      row.salePrice = v === "" ? 0 : v;
      const basic = basicFromSaleEditable(v || 0, gstPct);
      row.basicPrice = basic;
      row.price = basic;
    } else {
      row[name] = v === "" ? 0 : v;
    }

    // BASIC TOTAL = basic * qty
    row.taxableAmount = round2((row.qty || 0) * (row.basicPrice || 0));

    list[index] = row;

    const gstdetails = GstCalculation(
      Party,
      list,
      Context,
      selectedDiscountRows
    );
    setGSTData(gstdetails);

    if (gstdetails?.gstDetails && gstdetails?.gstDetails[index]) {
      row.sgstRate = gstdetails.gstDetails[index]?.sgstRate || 0;
      row.cgstRate = gstdetails.gstDetails[index]?.cgstRate || 0;
      row.igstRate = gstdetails.gstDetails[index]?.igstTax?.[0]?.amount || 0;
      row.grandTotal = gstdetails.gstDetails[index]?.grandTotal || 0;
    }

    list[index] = row;
    setProduct(list);

    if (CustomerTerm !== "Cash") {
      if ((gstdetails?.Tax?.GrandTotal || 0) < (CustomerLimit || 0)) {
        setBorderColor("green");
      } else {
        setBorderColor(emergencyStatus === "Active" ? "green" : "red");
      }
    }
    recalcTotals();
  };

  /* ---------------- Add/Remove product rows ---------------- */
  const addMoreProduct = () => {
    setProduct([
      ...product,
      {
        productId: "",
        productData: "",
        disCountPercentage: 0,
        availableQty: 0,
        wholeQty: 0,
        qty: 1,
        mrp: 0,
        gradeDiscountPct: 0,
        price: 0,
        basicPrice: 0,
        salePrice: 0,
        Size: 0,
        unitType: "",
        unitPriceAfterDiscount: "",
        totalprice: "",
        taxableAmount: 0,
        gstPercentage: 0,
        sgstRate: 0,
        cgstRate: 0,
        igstRate: 0,
        grandTotal: 0,
        HSN_Code: "",
        secondaryUnit: "",
        GrossQty: 0,
        primaryUnit: "",
      },
    ]);
  };

  const removeMoreProduct = (i) => {
    const newFormValues = [...product];
    newFormValues.splice(i, 1);
    setProduct(newFormValues);
    const gstdetails = GstCalculation(
      Party,
      newFormValues,
      Context,
      selectedDiscountRows
    );
    setGSTData(gstdetails);
    recalcTotals();
  };

  /* ---------------- Adjustments handlers ---------------- */
  const addChargeRow = () =>
    setSelectedChargesRows((prev) => [
      ...prev,
      {
        id: "",
        title: "",
        type: "Charges",
        discounttype: "",
        percentage: "",
        amount: "",
        __applied: 0,
      },
    ]);

  const addDiscountRow = () =>
    setSelectedDiscountRows((prev) => [
      ...prev,
      {
        id: "",
        title: "",
        type: "Discount",
        discounttype: "",
        percentage: "",
        amount: "",
        __applied: 0,
      },
    ]);

  const removeChargeRow = (i) => {
    const arr = [...selectedChargesRows];
    arr.splice(i, 1);
    setSelectedChargesRows(arr);
    recalcTotals(arr, selectedDiscountRows);
  };

  const removeDiscountRow = (i) => {
    const arr = [...selectedDiscountRows];
    arr.splice(i, 1);
    setSelectedDiscountRows(arr);
    const gstdetails = GstCalculation(Party, product, Context, arr);
    setGSTData(gstdetails);
    recalcTotals(selectedChargesRows, arr);
  };

  const pickMasterRow = (rowType, idx, id) => {
    const master = chargesMaster.find((x) => x._id === id);
    if (!master) return;

    const rowData = {
      id: master._id,
      title: master.title,
      type: master.type,
      discounttype: master.discounttype,
      percentage: master.percentage || "",
      amount: master.amount || "",
      __applied: 0,
    };

    if (rowType === "Charges") {
      const arr = [...selectedChargesRows];
      arr[idx] = rowData;
      setSelectedChargesRows(arr);
      recalcTotals(arr, selectedDiscountRows);
    } else {
      const arr = [...selectedDiscountRows];
      arr[idx] = rowData;
      const gstdetails = GstCalculation(Party, product, Context, arr);
      setGSTData(gstdetails);
      setSelectedDiscountRows(arr);
      recalcTotals(selectedChargesRows, arr);
    }
  };

  const changeRowField = (rowType, idx, key, value) => {
    const norm = (k, v) => {
      if (k === "percentage" || k === "amount") {
        if (v === "" || v === null) return "";
        const n = Number(v);
        return isNaN(n) ? "" : n;
      }
      return v;
    };

    if (rowType === "Charges") {
      const arr = [...selectedChargesRows];
      arr[idx] = { ...arr[idx], [key]: norm(key, value) };
      setSelectedChargesRows(arr);
      recalcTotals(arr, selectedDiscountRows);
    } else {
      const arr = [...selectedDiscountRows];
      arr[idx] = { ...arr[idx], [key]: norm(key, value) };
      setSelectedDiscountRows(arr);
      const gstdetails = GstCalculation(Party, product, Context, arr);
      setGSTData(gstdetails);
      recalcTotals(selectedChargesRows, arr);
    }
  };

  const onAddAdjustmentSelect = (e) => {
    const id = e.target.value;
    if (!id) return;
    e.target.value = "";
    const master = chargesMaster.find((x) => x._id === id);
    if (!master) return;

    const rowData = {
      id: master._id,
      title: master.title,
      type: master.type,
      discounttype: master.discounttype,
      percentage: master.percentage || "",
      amount: master.amount || "",
      __applied: 0,
    };

    if ((master.type || "").toLowerCase().includes("discount")) {
      const arr = [...selectedDiscountRows, rowData];
      setSelectedDiscountRows(arr);
      const gstdetails = GstCalculation(Party, product, Context, arr);
      setGSTData(gstdetails);
      recalcTotals(selectedChargesRows, arr);
    } else {
      const arr = [...selectedChargesRows, rowData];
      setSelectedChargesRows(arr);
      recalcTotals(arr, selectedDiscountRows);
    }
  };

  /* ---------------- Force Recalculate ---------------- */
  const forceRecalc = () => {
    const gstdetails = GstCalculation(
      Party,
      product,
      Context,
      selectedDiscountRows
    );
    setGSTData(gstdetails);
    recalcTotals(selectedChargesRows, selectedDiscountRows);
  };

  /* ---------------- Submit (invoice + order update) ---------------- */
  const handleProceed = async () => {
    try {
      setLoading(true);

      // REQUIRE a delivery boy selection
      if (!deliveryBoy) {
        setLoading(false);
        swal("Please select a Delivery Boy from the dropdown.");
        return;
      }

      if (borderColor === "red" && emergencyStatus !== "Active") {
        setLoading(false);
        swal(
          "Limit exceeded",
          "Request an emergency invoice and wait for approval.",
          "warning"
        );
        return;
      }

      const basicTotal = sumLineBasics(product);

      const {
        chargesTotal,
        discountTotal,
        discountRowsApplied,
        chargeRowsApplied,
        netAfterAdj,
      } = computeAdjustments(
        basicTotal,
        selectedDiscountRows,
        selectedChargesRows
      );

      const igstTaxType = !!GSTData?.Tax?.IgstTaxType;

      const highestGstPct = (() => {
        let maxPct = 0;
        (product || []).forEach((p) => {
          const g = parseGstPct(p?.gstPercentage);
          if (!isNaN(g) && g > maxPct) maxPct = g;
        });
        (GSTData?.gstDetails || []).forEach((row) => {
          const g = parseGstPct(row?.gstPercentage);
          if (!isNaN(g) && g > maxPct) maxPct = g;
        });
        return maxPct || 0;
      })();

      let chargesCgst = 0,
        chargesSgst = 0,
        chargesIgst = 0;
      if (chargesTotal > 0 && highestGstPct > 0) {
        const taxOnCharges = (chargesTotal * highestGstPct) / 100;
        if (igstTaxType) chargesIgst = taxOnCharges;
        else {
          chargesCgst = taxOnCharges / 2;
          chargesSgst = taxOnCharges / 2;
        }
      }

      const baseSgst = Number(GSTData?.Tax?.SgstTotal || 0);
      const baseCgst = Number(GSTData?.Tax?.CgstTotal || 0);
      const baseIgst = Number(GSTData?.Tax?.IgstTotal || 0);
      const roundOff = Number(GSTData?.Tax?.RoundOff || 0);

      const finalSgst = igstTaxType ? 0 : baseSgst + chargesSgst;
      const finalCgst = igstTaxType ? 0 : baseCgst + chargesCgst;
      const finalIgst = igstTaxType ? baseIgst + chargesIgst : 0;

      const itemsGrand = Array.isArray(GSTData?.gstDetails)
        ? GSTData.gstDetails.reduce(
            (acc, row) => acc + (Number(row?.grandTotal) || 0),
            0
          )
        : 0;

      const grandTotal =
        itemsGrand +
        chargesTotal +
        chargesCgst +
        chargesSgst +
        chargesIgst +
        roundOff;

      const orderItemsPayload = product.map((ele) => {
        const qty = Number(ele?.qty) || 0;
        const base = Number(ele?.basicPrice || ele?.price || 0);
        return {
          productId: ele?.productId,
          discountPercentage: Number(ele?.disCountPercentage) || 0,
          qty,
          price: base,
          salePrice: Number(ele?.salePrice) || 0,
          primaryUnit: ele?.primaryUnit,
          secondaryUnit: ele?.secondaryUnit,
          secondarySize: ele?.secondarySize,
          totalPrice: Number(qty * base),
          sgstRate: Number(ele?.sgstRate) || 0,
          cgstRate: Number(ele?.cgstRate) || 0,
          gstPercentage: parseGstPct(ele?.gstPercentage),
          igstRate: Number(ele?.igstRate) || 0,
          grandTotal: Number(ele?.grandTotal) || 0,
          taxableAmount: Number(qty * Number(ele?.basicPrice || base)),
          totalPriceWithDiscount: Number(ele?.grandTotal) || 0,
        };
      });

      const arnNumber = Number(grandTotal.toFixed(2));
      const ARNStatus = arnNumber > 49999;

      const fullname = Party?.CompanyName || Party?.fullName || "";
      const userData = JSON.parse(localStorage.getItem("userData")) || {};

      const discountDetails = [
        ...discountRowsApplied.map((x) => ({
          ...x,
          appliedAmount: x.__applied,
        })),
        ...chargeRowsApplied.map((x) => ({
          ...x,
          appliedAmount: x.__applied,
        })),
      ];

      // ----------------- 1) CREATE INVOICE -----------------
      const invoicePayload = {
        userId: Party?.created_by?._id,
        partyId: Party?._id || PartyId,
        invoiceId: InvoiceNumber,
        basicPrice: Number(netAfterAdj.toFixed(2)), // Taxable Value
        database: userData?.database,
        ARN: ARNStatus ? ARN : "",
        ARNStatus,
        discountPercentage: Party?.category
          ? Party?.category?.discount || 0
          : 0,
        SuperAdmin: userData?.created_by || userData?._id,
        fullName: fullname,
        address: Party?.address,
        igstTaxType,
        grandTotal: Number(grandTotal.toFixed(2)),
        roundOff: Number(roundOff.toFixed(2)),
        ChargesCgst: Number(chargesCgst.toFixed(2)),
        ChargesSgst: Number(chargesSgst.toFixed(2)),
        ChargesIgst: Number(chargesIgst.toFixed(2)),
        amount: Number(netAfterAdj.toFixed(2)),
        sgstTotal: Number(finalSgst.toFixed(2)),
        cgstTotal: Number(finalCgst.toFixed(2)),
        igstTotal: Number(finalIgst.toFixed(2)),
        gstDetails: GSTData?.gstDetails || [],
        MobileNo: Party?.contactNumber,
        pincode: Party?.pincode,
        state: Party?.State,
        date: orderDate,
        city: Party?.City,
        orderItems: orderItemsPayload,
        DateofDelivery: "",
        geotagging: geotagging,
        discountDetails,

        // NEW FIELDS
        NoOfPackage: Number(NoOfPackage || 0),
        deliveryBoy: deliveryBoy || "",
        AssignDeliveryBoy: deliveryBoy || "", // <--- added
        sourceOrderId: orderId || "",
        status: "Dispatch",
        isDelivered: "No", // <--- added
      };

      if (!invoicePayload.partyId) {
        swal("Select a customer first.");
        setLoading(false);
        return;
      }
      if (!invoicePayload.invoiceId) {
        swal("Enter an invoice number.");
        setLoading(false);
        return;
      }

      await _PostSave(Create_Existing_Invoice, invoicePayload);

      // ----------------- 2) UPDATE ORDER -----------------
      const orderUpdatePayload = {
        partyId: invoicePayload.partyId,
        orderItems: orderItemsPayload,
        basicPrice: invoicePayload.basicPrice,
        roundOff: invoicePayload.roundOff,
        igstTaxType,
        sgstTotal: invoicePayload.sgstTotal,
        cgstTotal: invoicePayload.cgstTotal,
        igstTotal: invoicePayload.igstTotal,
        grandTotal: invoicePayload.grandTotal,
        discountDetails: discountDetails,
        NoOfPackage: Number(NoOfPackage || 0),
        deliveryBoy: deliveryBoy,
        AssignDeliveryBoy: deliveryBoy, // <--- added
        isDelivered: "No", // <--- added
        status: "DELIVERY BOY ASSIGNED",
      };

      await _PutUpdate(Sales_Edit_Order2, orderId, orderUpdatePayload);

      // ----------------- EMERGENCY STATUS CLEANUP ---------------
      if (emergencyInvoice || emergencyStatus === "Active") {
        const Payload = {
          count: 0,
          status: "Completed",
          database: userData?.database,
          remark: remarkMessage,
          partyId: Party?._id || PartyId,
        };
        await _PutUpdate(updateEmergencyInvoice, lastEntryId, Payload);
        setEmergencyStatus("Completed");
        setEmergencyInvoice(false);
      }

      swal("Invoice Created, Order Updated (Delivery Boy Assigned).");
      clearSalesFastCaches();
      navigate("/app/softNumen/order/confirmedOrder");
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || "SomeThing Went Wrong";
      swal(msg);
    } finally {
      setLoading(false);
    }
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    handleProceed();
  };

  /* ---------------- init loads — optimized for SPEED ---------------- */
  useEffect(() => {
    let unsub = false;
    (async () => {
      try {
        setPageLoading(true);

        // Geotag (non-blocking)
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              geotagging = `${position.coords.latitude},${position.coords.longitude}`;
            },
            () => {},
            { timeout: 5000, enableHighAccuracy: false }
          );
        }

        const userInfo = JSON.parse(localStorage.getItem("userData"));
        setUserInfo(userInfo);

        // Instant CACHE hydrate (does not block UI)
        const cacheProducts = getCache("cache:ProductList");
        const cacheParties = getCache("cache:PartyList");
        const cacheUnits = getCache("cache:UnitList");
        if (cacheProducts) setProductList(cacheProducts);
        if (cacheParties) setPartyList(cacheParties);
        if (cacheUnits) setUnitList(cacheUnits);

        // If party is a Customer login, prefill
        const findParty = userInfo?.rolename?.roleName === "Customer";
        if (findParty) {
          setPartyLogin(true);
          setPartyId(userInfo?._id);
          const label =
            `${userInfo?.CompanyName || userInfo?.fullName || "Party"}` +
            `${
              userInfo?.category?.grade
                ? ` (Grade: ${String(userInfo?.category?.grade).toUpperCase()})`
                : ""
            }`;
          setParty({ ...userInfo, displayLabel: label });
        }

        // Fetch Sales bootstrap + the single order in parallel.
        // Delivery-boy data is intentionally non-blocking so it cannot delay
        // the invoice page becoming usable.
        const hierarchyRoot =
          userInfo?.rolename?.roleName === "SuperAdmin"
            ? userInfo?._id
            : userInfo?.created_by || userInfo?._id;

        CreateAccountList(userInfo?._id, userInfo?.database)
          .then((delivery) => {
            const raw = delivery?.adminDetails || [];
            const boys = raw.filter(
              (u) => u?.rolename?.roleName === "Delivery Boy",
            );
            if (!unsub) setDeliveryBoyOptions(boys || []);
          })
          .catch(() => {});

        const [fastRes, orderFastRes, customerRes] = await Promise.allSettled([
          getSalesPageBootstrapFast(userInfo?.database, hierarchyRoot),
          _Get("/order/sales-order-by-id/", orderId),
          getSalesCustomerOptions(userInfo?.database, hierarchyRoot),
        ]);

        const fast = fastRes.status === "fulfilled" ? fastRes.value || {} : {};
        const productsArr = Array.isArray(fast?.products) ? fast.products : [];
        const partyListRaw =
          customerRes.status === "fulfilled" && Array.isArray(customerRes.value)
            ? customerRes.value
            : Array.isArray(cacheParties)
              ? cacheParties
              : [];
        const unitsArr = Array.isArray(fast?.units) ? fast.units : [];
        const chargesArr = Array.isArray(fast?.otherCharges)
          ? fast.otherCharges
          : [];

        if (!unsub) {
          setProductList(productsArr);
          setUnitList(unitsArr);
          setChargesMaster(chargesArr);
        }
        setCache("cache:ProductList", productsArr);
        setCache("cache:UnitList", unitsArr);

        const formattedParties = partyListRaw.map(formatSalesCustomerOption);
        if (!unsub) setPartyList(formattedParties);
        setCache("cache:PartyList", formattedParties);

        // Pick order
        let picked = null;
        if (orderFastRes.status === "fulfilled") {
          const or = orderFastRes.value;
          picked =
            or?.order ||
            or?.orderHistory ||
            (Array.isArray(or?.data) ? or.data[0] : or?.data) ||
            or;
          if (Array.isArray(picked)) picked = picked[0];
        }
        if (!picked) {
          const ohRes = await _Get("/order/sales-order-by-id/", orderId);
          picked = ohRes?.orderHistory || ohRes?.order || ohRes?.Order || null;
        }

        if (!picked) {
          setPageLoading(false);
          swal("Order not found", "Cannot prefill the invoice.", "warning");
          return;
        }

        // Prefill: order number (READONLY)
        setOrderNumber(String(picked?.orderNo || picked?.orderNumber || ""));

        // Prefill: party
        const pickedPartyId = String(
          picked?.partyId?._id || picked?.partyId || "",
        );
        let partyToUse =
          formattedParties.find(
            (p) => String(p?._id) === pickedPartyId,
          ) || null;

        if (!partyToUse && pickedPartyId) {
          try {
            const balanceResult = await getSalesCustomerBalance(
              pickedPartyId,
              selectedSalesDatabase(userInfo?.database),
            );
            if (balanceResult?.customer) {
              partyToUse = formatSalesCustomerOption(balanceResult.customer);
              if (!unsub) {
                setPartyList((prev) => {
                  if (
                    prev.some(
                      (item) => String(item?._id) === pickedPartyId,
                    )
                  ) {
                    return prev;
                  }
                  return [partyToUse, ...prev];
                });
              }
            }
          } catch {}
        }

        if (partyToUse) {
          const label =
            (partyToUse?.displayLabel ||
              `${partyToUse?.CompanyName || partyToUse?.fullName || "Party"}`) +
            `${
              partyToUse?.category?.grade
                ? ` (Grade: ${String(
                    partyToUse?.category?.grade
                  ).toUpperCase()})`
                : ""
            }`;
          setPartyId(partyToUse._id);
          setParty({ ...partyToUse, displayLabel: label });
        }

        // Prefill: date
        const d = picked?.date || new Date().toISOString().slice(0, 10);
        setOrderDate(String(d).slice(0, 10));

        // Prefill: NoOfPackage + Delivery Boy (accept both id or name)
        setNoOfPackage(Number(picked?.NoOfPackage || 0));
        const preDB = picked?.AssignDeliveryBoy || picked?.deliveryBoy || "";
        if (preDB) setDeliveryBoy(String(preDB));

        // Prefill: products from orderItems
        const items = Array.isArray(picked?.orderItems)
          ? picked.orderItems
          : [];
        const mapped = items.map((it) => {
          const prod = productsArr.find(
            (p) => p?._id === (it?.productId?._id || it?.productId)
          );
          const mrp = Number(prod?.Product_MRP || 0);
          const gstPct =
            parseGstPct(
              prod?.GSTRate != null
                ? prod.GSTRate
                : it?.gstPercentage != null
                ? it.gstPercentage
                : 0
            ) || 0;

          const base = Number(it?.price || 0);
          const sale = saleFromBasic(base, gstPct);

          return {
            productId: it?.productId?._id || it?.productId || "",
            productData: prod || it?.productId || "",
            disCountPercentage: Number(it?.discountPercentage) || 0,
            availableQty: prod?.qty || 0,
            wholeQty: prod?.qty || 0,
            qty: Number(it?.qty || 1),
            mrp,
            gradeDiscountPct: Number(
              (partyToUse?.category?.discount ?? Party?.category?.discount) || 0
            ),
            price: base,
            basicPrice: base,
            salePrice: sale,
            Size: 0,
            unitType: "",
            unitPriceAfterDiscount: "",
            totalprice: "",
            taxableAmount: round2(Number(it?.qty || 1) * base),
            gstPercentage: gstPct,
            sgstRate: Number(it?.sgstRate || 0),
            cgstRate: Number(it?.cgstRate || 0),
            igstRate: Number(it?.igstRate || 0),
            grandTotal: Number(it?.grandTotal || 0),
            HSN_Code: prod?.HSN_Code || it?.HSN_Code || "",
            secondaryUnit: prod?.secondaryUnit || it?.secondaryUnit || "",
            secondarySize: prod?.secondarySize || it?.secondarySize || 0,
            GrossQty: Number(
              (
                Number(it?.qty || 0) /
                (prod?.secondarySize || it?.secondarySize || 1)
              ).toFixed(3)
            ),
            primaryUnit: prod?.primaryUnit || it?.primaryUnit || "",
          };
        });
        setProduct(mapped.length ? mapped : product);

        // Ledger/limits & invoice id
        if (partyToUse) {
          await handleSelectionParty([], partyToUse);
        } else {
          const nextInv = await unique3Digit();
          setInvoiceNumber(nextInv);
        }

        // Initial GST + totals
        const firstGST = GstCalculation(
          partyToUse || {},
          mapped.length ? mapped : product,
          Context,
          selectedDiscountRows
        );
        setGSTData(firstGST);
        recalcTotals(selectedChargesRows, selectedDiscountRows);

        setPageLoading(false);
      } catch (e) {
        console.error(e);
        swal("Init error", "Some data failed to load", "warning");
        setPageLoading(false);
      }
    })();
    return () => {
      unsub = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  useEffect(() => {
    const today = new Date();
    const formattedDate = today.toISOString().slice(0, 10);
    setOrderDate((prev) => prev || formattedDate);
    const userInfo = JSON.parse(localStorage.getItem("userData"));
    setUserInfo(userInfo);
  }, []);

  /* ---------- Emergency tracker ------------------------ */
  const toggleRemarkModal = () => setRemarkModel(!remarkModel);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    const selectedPartyId = Party?._id || PartyId;
    if (!userData?.database || !selectedPartyId) {
      setEmergencyStatus("");
      return;
    }

    const url = `${viewEmergencyInvoice}${encodeURIComponent(
      userData.database,
    )}?partyId=${encodeURIComponent(selectedPartyId)}`;

    _Get(url, "")
      .then((res) => {
        const latest = Array.isArray(res?.emergencyInvoice)
          ? res.emergencyInvoice[0]
          : null;
        setLastEntryId(latest?._id || "");
        setEmergencyStatus(latest?.status || "");
        if (latest?.status === "Active") setBorderColor("green");
      })
      .catch(() => setEmergencyStatus(""));
  }, [Party?._id, PartyId, emergencyInvoice]);

  /* ---------- Selected values for showing names in Multiselects ------ */
  const selectedPartyValue = useMemo(() => {
    if (!Party || !Party._id) return [];
    const obj = (PartyList || []).find((x) => x._id === Party._id);
    return obj ? [obj] : [];
  }, [Party, PartyList]);

  const getSelectedProductValue = (row) => {
    const pid = row?.productId;
    if (!pid) return [];
    const obj = (ProductList || []).find((x) => x._id === pid);
    return obj ? [obj] : [];
  };

  /* Recalculate once after the order/bootstrap data has been applied. */
  useEffect(() => {
    if (pageLoading) return undefined;
    const timer = setTimeout(() => forceRecalc(), 100);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageLoading]);

  /* ------------------------------ UI ------------------------------ */
  const styles = {
    headerRow: { margin: "0.5rem" },
    title: { margin: 0, fontSize: 22, fontWeight: 600 },
    right: { display: "flex", justifyContent: "flex-end", gap: 8 },
    smallNoteBlue: { fontSize: 12, color: "blue", cursor: "pointer" },
  };

  return (
    <div className="invoice-ui compact-ui">
      {/* Full page loader */}
      {pageLoading && (
        <div className="invoice-loader">
          <Spinner />
          <div style={{ fontWeight: 600 }}>Loading invoice...</div>
        </div>
      )}

      <Card>
        <Row style={styles.headerRow}>
          <Col>
            <h1 style={styles.title}>Create Sales Order Invoice</h1>
          </Col>
          <Col style={styles.right}>
            <Button color="secondary" outline onClick={forceRecalc}>
              Force Recalculate
            </Button>
            <Button color="danger" onClick={() => navigate(-1)}>
              Back
            </Button>
          </Col>
        </Row>

        <CardBody>
          <Form onSubmit={submitHandler}>
            {/* ================= TOP STRIP ================= */}
            {!PartyLogin && (
              <div className="top-strip">
                {/* Choose Party */}
                <div className="box party-box">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <Label>
                      Choose Party <span style={{ color: "red" }}>*</span>
                    </Label>
                    <div className="d-flex gap-1">
                      <Button
                        size="sm"
                        color="secondary"
                        type="button"
                        className="btn-xxs"
                        onClick={() => {
                          if (PartyId)
                            navigate(`/app/softNumen/customer/edit/${PartyId}`);
                          else swal("Select a customer first");
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        color="primary"
                        type="button"
                        className="btn-xxs"
                        onClick={() =>
                          navigate(`/app/softNumen/customer/create`)
                        }
                      >
                        New
                      </Button>
                    </div>
                  </div>

                  <Multiselect
                    required
                    selectionLimit={1}
                    isObject
                    options={PartyList}
                    onSelect={handleSelectionParty}
                    onRemove={onRemoveParty}
                    onSearch={handlePartySearch}
                    displayValue="displayLabel"
                    selectedValues={selectedPartyValue}
                  />

                  {(borderColor === "red" || emergencyStatus) && (
                    <small
                      style={styles.smallNoteBlue}
                      onClick={() => setRemarkModel(true)}
                      title="Click to request or view status"
                    >
                      Emergency invoice:&nbsp;
                      <span
                        style={{
                          color:
                            emergencyStatus === "Active"
                              ? "green"
                              : emergencyStatus === "Pending"
                              ? "orange"
                              : emergencyStatus === "Completed"
                              ? "green"
                              : "red",
                          fontWeight: 600,
                        }}
                      >
                        {emergencyStatus || "Click to request"}
                      </span>
                    </small>
                  )}
                </div>

                <div className="box orderdate-box">
                  <Label>
                    Order Date <span style={{ color: "red" }}>*</span>
                  </Label>
                  <Input
                    required
                    type="date"
                    name="orderDate"
                    value={orderDate}
                    onChange={(e) => setOrderDate(e.target.value)}
                  />
                </div>

                <div className="box invoiceno-box">
                  <Label>
                    Invoice Number <span style={{ color: "red" }}>*</span>
                  </Label>
                  <Input
                    required
                    type="text"
                    placeholder="Enter Invoice Number"
                    name="InvoiceNumber"
                    value={InvoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    onBlur={async () => {
                      if (!InvoiceNumber?.trim()) {
                        const u = await unique3Digit();
                        setInvoiceNumber(u);
                      }
                    }}
                  />
                </div>

                {/* Readonly Order Number */}
                <div className="box orderno-box">
                  <Label>Order Number</Label>
                  <Input readOnly type="text" value={OrderNumber} />
                </div>

                <div className="box packages-box">
                  <Label>No. of Packages</Label>
                  <Input
                    type="number"
                    min={0}
                    step="1"
                    value={NoOfPackage}
                    onChange={(e) =>
                      setNoOfPackage(
                        e.target.value === "" ? 0 : Number(e.target.value)
                      )
                    }
                  />
                </div>

                {/* Delivery Boy — REQUIRED */}
                <div className="box deliveryboy-box">
                  <Label>
                    Delivery Boy <span style={{ color: "red" }}>*</span>
                  </Label>
                  <Input
                    required
                    type="select"
                    value={deliveryBoy}
                    onChange={(e) => setDeliveryBoy(e.target.value)}
                  >
                    <option value="">-- Select --</option>
                    {(Array.isArray(deliveryBoyOptions)
                      ? deliveryBoyOptions
                      : []
                    ).map((boy) => (
                      <option key={boy?._id} value={boy?._id}>
                        {boy?.firstName ||
                          boy?.fullName ||
                          boy?.CompanyName ||
                          boy?.mobileNo ||
                          "Delivery Boy"}
                      </option>
                    ))}
                  </Input>
                </div>

                {/* Credit customer blocks */}
                {CustomerLimit > 0 ? (
                  <>
                    <div className="box sanction-box">
                      <Label>Sanction Amount</Label>
                      <Input
                        readOnly
                        type="text"
                        name="CustomerLimit"
                        value={Number(openingLimit || 0).toFixed(2)}
                      />
                      <div className="note-red">Ledger B: {ledgerBalance}</div>
                    </div>

                    <div className="box pending-box">
                      <Label>Pending Amount</Label>
                      <Input
                        readOnly
                        type="text"
                        value={Number(pendingData || 0).toFixed(2)}
                      />
                      <div className="note-red">Pending Sales</div>
                    </div>

                    <div className="box balance-box">
                      <Label>Balance Amount</Label>
                      <Input
                        readOnly
                        type="text"
                        value={Number(CustomerLimit || 0).toFixed(2)}
                      />
                    </div>

                    <div className="box netbalance-box">
                      <Label style={{ color: borderColor }}>Net Balance</Label>
                      <Input
                        readOnly
                        type="text"
                        style={{ border: `3px solid ${borderColor}` }}
                        value={
                          !!GSTData?.Tax?.GrandTotal
                            ? (
                                CustomerLimit - (GSTData?.Tax?.GrandTotal || 0)
                              ).toFixed(2)
                            : CustomerLimit
                        }
                      />
                    </div>
                  </>
                ) : (
                  <div className="box balance-box">
                    <Label>Payment Mode *</Label>
                    <Input readOnly type="text" value={CustomerTerm} />
                  </div>
                )}
              </div>
            )}

            {/* ================= PRODUCT ROWS ================= */}
            {product?.length > 0 &&
              product?.map((p, index) => (
                <div key={index} className="prod-row mt-2">
                  <div className="box prod-select-box">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <Label>
                        Select Product <span style={{ color: "red" }}>*</span>
                      </Label>
                      <div className="d-flex gap-1">
                        <Button
                          size="sm"
                          color="secondary"
                          type="button"
                          className="btn-xxs"
                          onClick={() => {
                            const pid = product?.[index]?.productId;
                            if (pid)
                              navigate(`/app/softNumen/product/edit/${pid}`);
                            else swal("Select a product first");
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          color="primary"
                          type="button"
                          className="btn-xxs"
                          onClick={() =>
                            navigate(`/app/softNumen/product/create`)
                          }
                        >
                          New
                        </Button>
                      </div>
                    </div>

                    <Multiselect
                      required
                      ref={multiselectRefs.current[index]}
                      selectionLimit={1}
                      isObject
                      options={ProductList}
                      onSelect={(sl, si) => handleSelection(sl, si, index)}
                      onRemove={(sl, si) => onRemove1(sl, si, index)}
                      displayValue="Product_Title"
                      selectedValues={getSelectedProductValue(p)}
                    />
                  </div>

                  <div className="box hsn-box">
                    <Label>HSN Code</Label>
                    <Input readOnly value={p?.HSN_Code || ""} />
                  </div>

                  <div className="box grossunit-box">
                    <Label>Gross Unit</Label>
                    <Input readOnly value={p?.secondaryUnit || ""} />
                  </div>

                  <div className="box grossqty-box">
                    <Label>Gross Qty</Label>
                    <Input
                      required
                      type="number"
                      step="any"
                      name="GrossQty"
                      value={p?.GrossQty || 0}
                      onChange={(e) =>
                        handleRequredQty(
                          e,
                          index,
                          p?.availableQty,
                          CustomerTerm
                        )
                      }
                    />
                  </div>

                  <div className="box availqty-box">
                    <Label>Avail Qty *</Label>
                    <Input readOnly value={p?.availableQty || 0} />
                  </div>

                  <div className="box netqty-box">
                    <Label>Net Qty *</Label>
                    <Input
                      required
                      type="number"
                      name="qty"
                      min={1}
                      value={p?.qty || 1}
                      onChange={(e) =>
                        handleRequredQty(
                          e,
                          index,
                          p?.availableQty,
                          CustomerTerm
                        )
                      }
                    />
                  </div>

                  <div className="box netunit-box">
                    <Label>Net Unit</Label>
                    <Input disabled value={p?.primaryUnit || ""} />
                  </div>

                  <div className="box mrp-box">
                    <Label>MRP</Label>
                    <Input readOnly value={(p?.mrp || 0).toFixed(3)} />
                  </div>

                  <div className="box gradedisc-box">
                    <Label>Grade Disc (%)</Label>
                    <Input readOnly value={p?.gradeDiscountPct || 0} />
                  </div>

                  <div className="box saleprice-box">
                    <Label>Sale Price (incl GST)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      name="salePrice"
                      value={p?.salePrice || 0}
                      onChange={(e) => handleChangePrice(e, index)}
                    />
                  </div>

                  <div className="box basicprice-box">
                    <Label>Basic Price (ex GST)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      name="basicPrice"
                      value={p?.basicPrice || 0}
                      onChange={(e) => handleChangePrice(e, index)}
                    />
                  </div>

                  <div className="box gst-box">
                    <Label>GST %</Label>
                    <Input disabled value={p?.gstPercentage || 0} />
                  </div>

                  <div className="box basictotal-box">
                    <Label>Basic Total</Label>
                    <Input
                      disabled
                      value={
                        p?.taxableAmount
                          ? Number(p?.taxableAmount).toFixed(2)
                          : 0
                      }
                    />
                  </div>

                  <div className="box remove-box">
                    {index ? (
                      <Button
                        type="button"
                        color="danger"
                        className="btn-xxs"
                        onClick={() => removeMoreProduct(index)}
                        style={{ marginTop: 24 }}
                        aria-label="Remove row"
                      >
                        X
                      </Button>
                    ) : null}
                  </div>
                </div>
              ))}

            <div className="d-flex justify-content-end mt-2">
              <Button color="primary" size="sm" onClick={addMoreProduct}>
                Add Product
              </Button>
            </div>

            {/* ================= BILL ADJUSTMENTS + HSN + TOTALS ================= */}
            <div className="justify-content-total jt-container mt-3">
              {/* LEFT: Adjustments */}
              <div className="left-tables jt-left">
                <div className="mini-title jt-title">Bill Adjustments</div>

                <div className="adjust-toolbar jt-toolbar">
                  <div className="box adjust-addselect jt-addselect">
                    <Label className="jt-addselect-label">
                      Add Discount / Charge
                    </Label>
                    <Input
                      type="select"
                      className="jt-addselect-input"
                      onChange={onAddAdjustmentSelect}
                    >
                      <option value="">-- Select from master --</option>
                      {chargesMaster.map((x) => (
                        <option key={x._id} value={x._id}>
                          {x.title} ({x.type})
                        </option>
                      ))}
                    </Input>
                  </div>

                  <Button
                    size="sm"
                    color="success"
                    className="btn-xxs jt-btn jt-btn-discount"
                    onClick={addDiscountRow}
                  >
                    + Discount
                  </Button>
                  <Button
                    size="sm"
                    color="primary"
                    className="btn-xxs jt-btn jt-btn-charge"
                    onClick={addChargeRow}
                  >
                    + Charge
                  </Button>
                </div>

                {/* Adjustments table */}
                <div className="adjust-table jt-table">
                  <div className="adj-row adj-head jt-row jt-head">
                    <div className="jt-h-idx">#</div>
                    <div className="jt-h-type">Type</div>
                    <div className="jt-h-title">Title / Master</div>
                    <div className="jt-h-method">Method</div>
                    <div className="jt-h-pct">%</div>
                    <div className="jt-h-amt">Amount</div>
                    <div className="jt-h-applied">Applied</div>
                    <div className="jt-h-action">Action</div>
                  </div>

                  {/* Discounts */}
                  {selectedDiscountRows.map((r, i) => {
                    const isPct =
                      (r.discounttype || "").toLowerCase() === "percentage";
                    return (
                      <div
                        className="adj-row jt-row jt-discount-row"
                        key={`disc-${i}`}
                      >
                        <div className="c-idx jt-idx">{i + 1}</div>
                        <div className="c-type jt-type">Discount</div>

                        <div className="c-title jt-title-cell">
                          <Input
                            type="select"
                            className="jt-input jt-select jt-title-select"
                            value={r.id || ""}
                            onChange={(e) =>
                              pickMasterRow("Discount", i, e.target.value)
                            }
                          >
                            <option value="">-- Select from master --</option>
                            {chargesMaster
                              .filter((m) =>
                                (m.type || "")
                                  .toLowerCase()
                                  .includes("discount")
                              )
                              .map((m) => (
                                <option key={m._id} value={m._id}>
                                  {m.title}
                                </option>
                              ))}
                          </Input>
                        </div>

                        <div className="c-method jt-method-cell">
                          <Input
                            type="select"
                            className="jt-input jt-select jt-method-select"
                            value={r.discounttype || ""}
                            onChange={(e) =>
                              changeRowField(
                                "Discount",
                                i,
                                "discounttype",
                                e.target.value
                              )
                            }
                          >
                            <option value="">Choose</option>
                            <option value="Percentage">Percentage</option>
                            <option value="Amount">Amount</option>
                          </Input>
                        </div>

                        <div className="c-pct jt-pct-cell">
                          <Input
                            type="number"
                            inputMode="decimal"
                            placeholder="%"
                            className="jt-input jt-number jt-pct-input"
                            disabled={!isPct}
                            value={r.percentage === "" ? "" : r.percentage}
                            onChange={(e) =>
                              changeRowField(
                                "Discount",
                                i,
                                "percentage",
                                e.target.value
                              )
                            }
                          />
                        </div>

                        <div className="c-amt jt-amt-cell">
                          <Input
                            type="number"
                            inputMode="decimal"
                            placeholder="₹"
                            className="jt-input jt-number jt-amt-input"
                            disabled={isPct}
                            value={r.amount === "" ? "" : r.amount}
                            onChange={(e) =>
                              changeRowField(
                                "Discount",
                                i,
                                "amount",
                                e.target.value
                              )
                            }
                          />
                        </div>

                        <div className="c-applied jt-applied-cell">
                          <Input
                            readOnly
                            className="jt-input jt-readonly jt-applied-input"
                            value={r.__applied ?? 0}
                          />
                        </div>

                        <div className="c-action jt-action-cell">
                          <Button
                            color="danger"
                            size="sm"
                            className="btn-xxs jt-btn jt-delete-btn"
                            onClick={() => removeDiscountRow(i)}
                            aria-label="Delete"
                          >
                            🗑
                          </Button>
                        </div>
                      </div>
                    );
                  })}

                  {/* Charges */}
                  {selectedChargesRows.map((r, i) => {
                    const isPct =
                      (r.discounttype || "").toLowerCase() === "percentage";
                    return (
                      <div
                        className="adj-row jt-row jt-charge-row"
                        key={`charge-${i}`}
                      >
                        <div className="c-idx jt-idx">{i + 1}</div>
                        <div className="c-type jt-type">Charge</div>

                        <div className="c-title jt-title-cell">
                          <Input
                            type="select"
                            className="jt-input jt-select jt-title-select"
                            value={r.id || ""}
                            onChange={(e) =>
                              pickMasterRow("Charges", i, e.target.value)
                            }
                          >
                            <option value="">-- Select from master --</option>
                            {chargesMaster
                              .filter((m) =>
                                (m.type || "").toLowerCase().includes("charge")
                              )
                              .map((m) => (
                                <option key={m._id} value={m._id}>
                                  {m.title}
                                </option>
                              ))}
                          </Input>
                        </div>

                        <div className="c-method jt-method-cell">
                          <Input
                            type="select"
                            className="jt-input jt-select jt-method-select"
                            value={r.discounttype || ""}
                            onChange={(e) =>
                              changeRowField(
                                "Charges",
                                i,
                                "discounttype",
                                e.target.value
                              )
                            }
                          >
                            <option value="">Choose</option>
                            <option value="Percentage">Percentage</option>
                            <option value="Amount">Amount</option>
                          </Input>
                        </div>

                        <div className="c-pct jt-pct-cell">
                          <Input
                            type="number"
                            inputMode="decimal"
                            placeholder="%"
                            className="jt-input jt-number jt-pct-input"
                            disabled={!isPct}
                            value={r.percentage === "" ? "" : r.percentage}
                            onChange={(e) =>
                              changeRowField(
                                "Charges",
                                i,
                                "percentage",
                                e.target.value
                              )
                            }
                          />
                        </div>

                        <div className="c-amt jt-amt-cell">
                          <Input
                            type="number"
                            inputMode="decimal"
                            placeholder="₹"
                            className="jt-input jt-number jt-amt-input"
                            disabled={isPct}
                            value={r.amount === "" ? "" : r.amount}
                            onChange={(e) =>
                              changeRowField(
                                "Charges",
                                i,
                                "amount",
                                e.target.value
                              )
                            }
                          />
                        </div>

                        <div className="c-applied jt-applied-cell">
                          <Input
                            readOnly
                            className="jt-input jt-readonly jt-applied-input"
                            value={r.__applied ?? 0}
                          />
                        </div>

                        <div className="c-action jt-action-cell">
                          <Button
                            color="danger"
                            size="sm"
                            className="btn-xxs jt-btn jt-delete-btn"
                            onClick={() => removeChargeRow(i)}
                            aria-label="Delete"
                          >
                            🗑
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {hsnSummary.rows.length > 0 && (
                <div className="mt-2 jt-hsn-block">
                  <div className="mini-title jt-hsn-title">HSN Summary</div>
                  <Table bordered size="sm" className="jt-hsn-table">
                    <thead className="jt-hsn-thead">
                      <tr className="jt-hsn-headrow">
                        <th className="jt-hsn-th jt-hsn-col-hsn">HSN/SAC</th>
                        <th className="jt-hsn-th jt-hsn-col-taxable text-end">
                          TAXABLE VALUE
                        </th>
                        <th className="jt-hsn-th jt-hsn-col-rate text-center">
                          TAX RATE
                        </th>
                        {!hsnSummary.isIGST && (
                          <th className="jt-hsn-th jt-hsn-col-cgst text-end">
                            CGST
                          </th>
                        )}
                        {!hsnSummary.isIGST && (
                          <th className="jt-hsn-th jt-hsn-col-sgst text-end">
                            SGST
                          </th>
                        )}
                        {hsnSummary.isIGST && (
                          <th className="jt-hsn-th jt-hsn-col-igst text-end">
                            IGST
                          </th>
                        )}
                        <th className="jt-hsn-th jt-hsn-col-total text-end">
                          TOTAL
                        </th>
                      </tr>
                    </thead>
                    <tbody className="jt-hsn-tbody">
                      {hsnSummary.rows.map((r, i) => (
                        <tr
                          className="jt-hsn-row"
                          key={`hsn-${r.kind}-${r.hsn}-${i}`}
                        >
                          <td className="jt-hsn-td jt-hsn-col-hsn">{r.hsn}</td>
                          <td className="jt-hsn-td jt-hsn-col-taxable text-end">
                            {r.taxable.toFixed(2)}
                          </td>
                          <td className="jt-hsn-td jt-hsn-col-rate text-center">
                            {`${r.ratePct}%`}
                          </td>
                          {!hsnSummary.isIGST && (
                            <td className="jt-hsn-td jt-hsn-col-cgst text-end">
                              {(r.cgst || 0).toFixed(2)}
                            </td>
                          )}
                          {!hsnSummary.isIGST && (
                            <td className="jt-hsn-td jt-hsn-col-sgst text-end">
                              {(r.sgst || 0).toFixed(2)}
                            </td>
                          )}
                          {hsnSummary.isIGST && (
                            <td className="jt-hsn-td jt-hsn-col-igst text-end">
                              {(r.igst || 0).toFixed(2)}
                            </td>
                          )}
                          <td className="jt-hsn-td jt-hsn-col-total text-end">
                            {r.total.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                      <tr className="jt-hsn-row jt-hsn-totalrow">
                        <td className="jt-hsn-td jt-hsn-col-hsn jt-bold">
                          TOTAL
                        </td>
                        <td className="jt-hsn-td jt-bold text-end">
                          {round2(hsnSummary.totals.taxable).toFixed(2)}
                        </td>
                        <td />
                        {!hsnSummary.isIGST && (
                          <td className="jt-hsn-td jt-bold text-end">
                            {round2(hsnSummary.totals.cgst).toFixed(2)}
                          </td>
                        )}
                        {!hsnSummary.isIGST && (
                          <td className="jt-hsn-td jt-bold text-end">
                            {round2(hsnSummary.totals.sgst).toFixed(2)}
                          </td>
                        )}
                        {hsnSummary.isIGST && (
                          <td className="jt-hsn-td jt-bold text-end">
                            {round2(hsnSummary.totals.igst).toFixed(2)}
                          </td>
                        )}
                        <td className="jt-hsn-td jt-bold text-end">
                          {round2(hsnSummary.totals.total).toFixed(2)}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </div>
              )}

              {/* RIGHT: Totals + Submit */}
              <div className="right-summary jt-right">
                <ul className="totals-ul jt-totals">
                  <li className="jt-total-row">
                    <span className="jt-total-label">Basic Total</span>
                    <strong className="jt-total-val">
                      {Number(sumLineBasics(product)).toFixed(2)}
                    </strong>
                  </li>
                  <li className="jt-total-row">
                    <span className="jt-total-label">Discount Total</span>
                    <strong className="jt-total-val">
                      {selectedDiscountRows
                        .reduce((a, r) => a + (Number(r.__applied) || 0), 0)
                        .toFixed(2)}
                    </strong>
                  </li>
                  <li className="jt-total-row">
                    <span className="jt-total-label">Charges Total</span>
                    <strong className="jt-total-val">
                      {selectedChargesRows
                        .reduce((a, r) => a + (Number(r.__applied) || 0), 0)
                        .toFixed(2)}
                    </strong>
                  </li>
                  <li className="jt-total-row">
                    <span className="jt-total-label">Taxable Value</span>
                    <strong className="jt-total-val">
                      {(finalPrice > 0
                        ? Number(finalPrice)
                        : Number(sumLineBasics(product))
                      ).toFixed(2)}
                    </strong>
                  </li>

                  {GSTData?.Tax?.IgstTaxType ? (
                    <li className="jt-total-row">
                      <span className="jt-total-label">
                        IGST (incl. on charges)
                      </span>
                      <strong className="jt-total-val">
                        {Number(
                          (
                            Number(GSTData?.Tax?.IgstTotal || 0) +
                            Number(taxRate || 0)
                          ).toFixed(2)
                        ).toFixed(2)}
                      </strong>
                    </li>
                  ) : (
                    <>
                      <li className="jt-total-row">
                        <span className="jt-total-label">
                          SGST (incl. on charges)
                        </span>
                        <strong className="jt-total-val">
                          {Number(
                            (
                              Number(GSTData?.Tax?.SgstTotal || 0) +
                              Number(taxRate || 0) / 2
                            ).toFixed(2)
                          ).toFixed(2)}
                        </strong>
                      </li>
                      <li className="jt-total-row">
                        <span className="jt-total-label">
                          CGST (incl. on charges)
                        </span>
                        <strong className="jt-total-val">
                          {Number(
                            (
                              Number(GSTData?.Tax?.CgstTotal || 0) +
                              Number(taxRate || 0) / 2
                            ).toFixed(2)
                          ).toFixed(2)}
                        </strong>
                      </li>
                    </>
                  )}

                  <li className="jt-total-row">
                    <span className="jt-total-label">RoundOff</span>
                    <strong className="jt-total-val">
                      {Number(localRoundOff || 0).toFixed(2)}
                    </strong>
                  </li>

                  <hr className="jt-hr" />
                  <li className="jt-total-row jt-grand">
                    <span className="jt-total-label">Grand Total</span>
                    <strong className="jt-total-val">
                      {Number(finalPriceAfterTax || 0).toFixed(2)}
                    </strong>
                  </li>
                </ul>

                {!Loading &&
                (borderColor !== "red" || emergencyStatus === "Active") ? (
                  <div className="d-flex justify-content-end jt-submit">
                    <Button
                      color="primary"
                      type="submit"
                      className="mt-2 jt-submit-btn"
                    >
                      Submit
                    </Button>
                  </div>
                ) : (
                  <div className="d-flex justify-content-end jt-submit">
                    <Button
                      disabled
                      color="secondary"
                      className="mt-2 jt-submit-btn"
                    >
                      {emergencyStatus === "Pending"
                        ? "Awaiting approval"
                        : borderColor === "red" && emergencyStatus !== "Active"
                        ? "Limit exceeded"
                        : "Submitting..."}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Form>
        </CardBody>
      </Card>

      {/* Emergency Remark Modal */}
      <Modal isOpen={remarkModel} toggle={() => setRemarkModel(!remarkModel)}>
        <ModalHeader toggle={() => setRemarkModel(!remarkModel)}>
          Enter Emergency Remark
        </ModalHeader>
        <ModalBody>
          <Label htmlFor="remark">Emergency Message</Label>
          <Input
            type="textarea"
            id="remark"
            placeholder="Enter your emergency message..."
            value={remarkMessage}
            onChange={(e) => setRemarkMessage(e.target.value)}
          />
          <div className="d-flex justify-content-end mt-2">
            <Button color="secondary" onClick={() => setRemarkModel(false)}>
              Cancel
            </Button>
            <Button
              color="primary"
              className="ms-2"
              onClick={async () => {
                const userData = JSON.parse(localStorage.getItem("userData"));
                const Payload = {
                  partyId: Party?._id ? Party?._id : PartyId,
                  count: 0,
                  database: userData?.database,
                  status: "Pending",
                  remark: remarkMessage,
                };
                const res = await _PostSave(EmergencyInvoice, Payload);
                if (res?.status) {
                  swal("Request for emergency invoice submitted");
                  setRemarkModel(false);
                  setRemarkMessage("");
                  setEmergencyInvoice(true);
                  setEmergencyStatus("Pending");
                } else {
                  swal("Error in emergency invoice");
                }
              }}
            >
              Submit
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default CreateSalesOrderInvoice;
